import React, { useEffect, useRef } from "react";
import { useAuth } from "@/auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { FiUsers, FiFileText, FiMessageSquare, FiClipboard, FiBriefcase, FiCheckCircle, FiSettings, FiUserPlus, FiBarChart2 } from "react-icons/fi";
import { Icons } from "../icons";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lottie from "lottie-react";
// import { LottieHeartbeat, LottieFloatingElements } from "../ui/lottie-animations";
// import FloatingActionButton from "../ui/floating-action-button";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Lottie animation data from LottieFiles - using popular animations
const welcomeAnimation = {
  v: "5.7.4",
  fr: 29.9700012207031,
  ip: 0,
  op: 120,
  w: 1080,
  h: 1080,
  nm: "Welcome",
  ddd: 0,
  assets: [],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: "Circle",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 1, k: [
          { i: { x: [0.667], y: [1] }, o: { x: [0.333], y: [0] }, t: 0, s: [0] },
          { t: 119, s: [360] }
        ], ix: 10 },
        p: { a: 0, k: [540, 540, 0], ix: 2 },
        a: { a: 0, k: [0, 0, 0], ix: 1 },
        s: { a: 1, k: [
          { i: { x: [0.667, 0.667, 0.667], y: [1, 1, 1] }, o: { x: [0.333, 0.333, 0.333], y: [0, 0, 0] }, t: 0, s: [0, 0, 100] },
          { i: { x: [0.667, 0.667, 0.667], y: [1, 1, 1] }, o: { x: [0.333, 0.333, 0.333], y: [0, 0, 0] }, t: 30, s: [120, 120, 100] },
          { t: 119, s: [100, 100, 100] }
        ], ix: 6 }
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            {
              d: 1,
              ty: "el",
              s: { a: 0, k: [200, 200], ix: 2 },
              p: { a: 0, k: [0, 0], ix: 3 },
              nm: "Ellipse Path 1",
              mn: "ADBE Vector Shape - Ellipse",
              hd: false
            },
            {
              ty: "fl",
              c: { a: 0, k: [0.2, 0.6, 1, 1], ix: 4 },
              o: { a: 0, k: 100, ix: 5 },
              r: 1,
              bm: 0,
              nm: "Fill 1",
              mn: "ADBE Vector Graphic - Fill",
              hd: false
            }
          ],
          nm: "Ellipse 1",
          np: 3,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: "ADBE Vector Group",
          hd: false
        }
      ],
      ip: 0,
      op: 120,
      st: 0,
      bm: 0
    }
  ],
  markers: []
};

const dashboardAnimation = {
  v: "5.7.4",
  fr: 30,
  ip: 0,
  op: 90,
  w: 400,
  h: 400,
  nm: "Dashboard",
  ddd: 0,
  assets: [],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: "Chart",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 0, ix: 10 },
        p: { a: 0, k: [200, 200, 0], ix: 2 },
        a: { a: 0, k: [0, 0, 0], ix: 1 },
        s: { a: 1, k: [
          { i: { x: [0.667, 0.667, 0.667], y: [1, 1, 1] }, o: { x: [0.333, 0.333, 0.333], y: [0, 0, 0] }, t: 0, s: [0, 0, 100] },
          { t: 45, s: [100, 100, 100] }
        ], ix: 6 }
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            {
              ty: "rc",
              d: 1,
              s: { a: 0, k: [300, 200], ix: 2 },
              p: { a: 0, k: [0, 0], ix: 3 },
              r: { a: 0, k: 20, ix: 4 },
              nm: "Rectangle Path 1",
              mn: "ADBE Vector Shape - Rect",
              hd: false
            },
            {
              ty: "fl",
              c: { a: 0, k: [0.1, 0.1, 0.1, 1], ix: 4 },
              o: { a: 0, k: 100, ix: 5 },
              r: 1,
              bm: 0,
              nm: "Fill 1",
              mn: "ADBE Vector Graphic - Fill",
              hd: false
            }
          ],
          nm: "Rectangle 1",
          np: 3,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: "ADBE Vector Group",
          hd: false
        }
      ],
      ip: 0,
      op: 90,
      st: 0,
      bm: 0
    }
  ],
  markers: []
};

export default function Home() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);

  const greetings = [
    { greeting: "Buongiorno", emoji: "☀️", fromHour: 6, toHour: 12 },
    { greeting: "Buon pomeriggio", emoji: "🌤️", fromHour: 12, toHour: 17 },
    { greeting: "Buonasera", emoji: "🌙", fromHour: 17, toHour: 6 },
  ];

  useEffect(() => {
    if (!loading && user && titleRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        // Create a timeline for smooth entrance animations
        const tl = gsap.timeline({ 
          defaults: { ease: "power2.out" }
        });

        // Animate the title
        tl.to(titleRef.current, {
          duration: 0.8,
          y: 0,
          opacity: 1,
          ease: "power2.out"
        })
        // Animate cards with stagger
        .to(cardsRef.current.filter(Boolean), {
          duration: 0.6,
          y: 0,
          opacity: 1,
          stagger: 0.1,
          ease: "power2.out"
        }, "-=0.3");

        // Store the timeline for cleanup
        return tl;
      }, 100);

      return () => {
        clearTimeout(timer);
        // Kill any running animations
        gsap.killTweensOf([titleRef.current, ...cardsRef.current.filter(Boolean)]);
      };
    }
  }, [loading, user]);

  // Hover animations removed for minimalist design

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-32 h-32">
        <Lottie
          animationData={welcomeAnimation}
          loop={true}
          autoplay={true}
        />
      </div>
    </div>
  );

  const name = user?.displayName?.split(" ")[0] || "Agente";
  const hour = new Date().getHours();
  const { greeting, emoji } =
    greetings.find((g) =>
      g.greeting === "Buonasera"
        ? hour >= 17 || hour < 6
        : hour >= g.fromHour && hour < g.toHour
    ) || greetings[0];

  // Admin cards with enhanced styling
  const adminCards = [
    {
      title: "Utenti",
      description: "Gestisci gli utenti del sistema",
      icon: <Icons.users className="w-6 h-6" />,
      path: "/users",
      size: "large",
      stats: "Gestisci permessi",
      disabled: false,
      gradient: "from-blue-500 to-purple-600",
      iconBg: "bg-blue-100"
    },
    {
      title: "Crea campagna email",
      description: "Crea una nuova campagna email",
      icon: <Icons.envelopeSimple className="w-6 h-6" />,
      path: "/email-marketing/create",
      size: "large",
      stats: "Crea una nuova campagna email",
      gradient: "from-green-500 to-teal-600",
      iconBg: "bg-green-100"
    },
    {
      title: "Impostazioni",
      description: "Configura il tuo Accelera CRM",
      icon: <Icons.gear className="w-6 h-6" />,
      path: "/settings",
      size: "medium",
      gradient: "from-gray-500 to-gray-600",
      iconBg: "bg-gray-100"
    },
    {
      title: "Leads",
      description: "Gestisci e monitora i Leads Campagne",
      icon: <Icons.megaphone className="w-6 h-6" />,
      path: "/leads-campagne",
      size: "large",
      disabled: false,
      gradient: "from-orange-500 to-red-600",
      iconBg: "bg-orange-100",
      // hasLottie: true,
      // lottieComponent: <LottieFloatingElements size={6} className="absolute top-2 right-2 opacity-60" />
    },
  ];

  // Agent cards with enhanced styling
  const agentCards = [
    {
      title: "Leads",
      description: "Gestisci e monitora i tuoi leads",
      icon: <Icons.megaphone className="w-6 h-6" />,
      path: "/agent/my-leads",
      size: "large",
      disabled: false,
      gradient: "from-blue-500 to-indigo-600",
      iconBg: "bg-blue-100"
    },
    {
      title: "Impostazioni",
      description: "Configura il tuo Accelera CRM",
      icon: <Icons.gear className="w-6 h-6" />,
      path: "/settings",
      size: "medium",
      gradient: "from-gray-500 to-slate-600",
      iconBg: "bg-gray-100"
    },
  ];

  // Backoffice cards with enhanced styling
  const backofficeCards = [
    {
      title: "Impostazioni",
      description: "Configura il tuo Accelera CRM",
      icon: <Icons.gear className="w-6 h-6" />,
      path: "/settings",
      size: "large",
      gradient: "from-gray-500 to-slate-600",
      iconBg: "bg-gray-100"
    },
  ];

  // Select cards based on user role
  const cards = user?.role === 'admin' ? adminCards : 
               user?.role === 'backoffice' ? backofficeCards : 
               agentCards;

  return (
    <div 
      ref={containerRef}
      className="flex flex-col h-full bg-gradient-to-br from-blue-50/30 via-purple-50/20 to-pink-50/30 min-h-screen relative overflow-hidden"
    >


      <div className="relative z-10 p-6 xl:mt-8 lg:mt-8">
        <div className="relative group">
          {/* Main Hero Card */}
          <div className="relative overflow-hidden rounded-2xl bg-blue-100/40 shadow-sm">
            {/* Inner content */}
            <div className="relative p-8 rounded-2xl">
              

              
              {/* Ultra dynamic content */}
              <div ref={titleRef} style={{ opacity: 0 }} className="relative">
                <div className="flex items-center justify-between mb-8">
                  
                  {/* Left side - Main greeting */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-6 mb-6">
                      {/* Emoji container */}
                      <div className="relative">
                        <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-blue-100/60 shadow-sm">
                          <span className="text-4xl">{emoji}</span>
                        </div>
                      </div>
                      
                      {/* Time & Date Section */}
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="px-3 py-1 rounded-full bg-green-100/60 shadow-sm">
                            <span className="text-xs font-bold text-green-700 uppercase tracking-wider">LIVE</span>
                          </div>
                          <div className="text-sm font-mono text-gray-600">
                            {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 font-medium mb-2">
                          {new Date().toLocaleDateString('it-IT', { 
                            weekday: 'long', 
                            day: 'numeric', 
                            month: 'long',
                            year: 'numeric'
                          })}
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold leading-tight text-gray-900">
                          {greeting}
                          <br />
                          {name}
                        </h1>
                      </div>
                    </div>
                    
                    {/* Status Bar */}
                    <div className="flex items-center space-x-8">
                      <div className="flex items-center space-x-3 px-4 py-2 rounded-full bg-green-100/60 shadow-sm">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-semibold text-gray-700">Sistema Operativo</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span className="text-sm font-medium">Accelera v2.0</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side - Stats */}
                  <div className="hidden xl:block">
                    <div className="grid grid-cols-3 gap-6">
                      {[
                        { label: 'Gestione Lead', value: 'Smart', icon: '🧠', bg: 'bg-blue-100/60' },
                        { label: 'Analytics', value: 'Real-time', icon: '⚡', bg: 'bg-green-100/60' }
                      ].map((stat, i) => (
                        <div key={i} className="text-center">
                          <div className="relative mb-3">
                            <div className={`relative w-16 h-16 mx-auto rounded-xl ${stat.bg} flex items-center justify-center shadow-sm`}>
                              <span className="text-2xl">{stat.icon}</span>
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                          <div className="text-xs text-gray-600 uppercase tracking-wider font-semibold">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Bottom Action Bar */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200/30">
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-300"></div>
                      <span className="text-sm text-gray-700 font-medium">CRM Intelligente</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-pink-300"></div>
                      <span className="text-sm text-gray-700 font-medium">Email Marketing Nurturing</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-cyan-300"></div>
                      <span className="text-sm text-gray-700 font-medium">Automazione</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 p-6 flex-1">
        <div className="grid grid-cols-12 gap-6">
          {cards.map((card, index) => (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              onClick={() => !card.disabled && navigate(card.path)}
              className={`col-span-12 ${
                card.size === "large" 
                  ? "md:col-span-6" 
                  : card.size === "medium" 
                  ? "md:col-span-4" 
                  : "md:col-span-3"
              } relative overflow-hidden rounded-xl shadow-sm ${
                card.disabled 
                  ? "opacity-50 cursor-not-allowed bg-gray-50/40" 
                  : "cursor-pointer bg-white/60"
              }`}
              style={{
                opacity: 0
              }}
            >
               <div className="relative p-6 h-full">
                <div className="flex flex-col h-full">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {card.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                    <div className={`p-3 rounded-xl ${card.disabled ? "bg-gray-100/60" : card.iconBg} ml-4 shadow-sm`}>
                      <div className="text-gray-700">
                        {card.icon}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-auto">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-500">
                        {card.stats}
                      </p>
                      {!card.disabled && (
                        <div className="w-8 h-8 rounded-full bg-gray-100/60 flex items-center justify-center shadow-sm">
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating Action Button for quick actions */}
      {/* <FloatingActionButton
        actions={[
          {
            label: "Nuovo Lead",
            icon: <Icons.userPlus className="w-5 h-5" />,
            onClick: () => navigate("/agent/my-leads")
          },
          {
            label: "Email Marketing Nurturing",
            icon: <Icons.envelopeSimple className="w-5 h-5" />,
            onClick: () => navigate("/email-marketing")
          }
        ]}
        primaryIcon={<Icons.plus className="w-6 h-6" />}
      /> */}
    </div>
  );
} 